import { Award, Crown, Star, User, Zap } from "lucide-react";

export const tabConfigs = [
    /* ===================== BASICS TAB ===================== */
    {
        id: 'basics',
        label: 'Options Flow Basics',
        subtitle: 'Master fundamentals of reading options flow signals',
        questions: [
            // 12 questions from Part 1 video (unchanged)
            {
                question: 'What is the primary purpose of scanning unusual options flow?',
                options: [
                    'Finding trades or trade sequences that act as indicators',
                    'Checking daily volume totals',
                    'Monitoring dividends',
                    'Tracking bond yields'
                ],
                answer: 'Finding trades or trade sequences that act as indicators',
                explanation:
                    'Unusual options flow focuses on identifying trades that deviate from normal activity, such as exceptionally large size or activity in seldom‑traded tickers.'
            },
            {
                question: 'Bullflow delivers real‑time flow data by leveraging which type of data source?',
                options: [
                    'Institutional‑grade exchange data',
                    'Delayed retail quotes',
                    'Social media sentiment',
                    'News headlines'
                ],
                answer: 'Institutional‑grade exchange data',
                explanation:
                    'Bullflow uses institutional‑grade data previously reserved for hedge funds and large institutions.'
            },
            {
                question: "The 'Value' column indicates _____?",
                options: [
                    'Dollar amount entered or exited in the trade',
                    'Implied volatility of the contract',
                    'Trade delta',
                    'Broker fees paid'
                ],
                answer: 'Dollar amount entered or exited in the trade',
                explanation:
                    'Value is simply how much money was put into (or taken out of) the option position.'
            },
            {
                question: 'What does the volume column represent?',
                options: [
                    'Contracts traded in that transaction',
                    'Total daily volume of the contract',
                    'Current open interest of the contract',
                    'Average volume over the past week'
                ],
                answer: 'Total daily volume of the contract',
                explanation:
                    'It shows the cumulative number of contracts traded for that option so far during the trading day, giving context on overall liquidity.'
            },
            {
                question: "In the Bullflow scanner, the 'Spot' field refers to _____?",
                options: [
                    'Underlying stock price at trade time',
                    'Option premium paid',
                    'Average fill price across exchanges',
                    'Opening price of the trading day'
                ],
                answer: 'Underlying stock price at trade time',
                explanation:
                    'Spot is defined as the price of the underlying security at the instant the option trade occurred.'
            },
            {
                question: 'What does the Open Interest (OI) column represent?',
                options: [
                    'The total number of outstanding contracts that have not been closed or exercised',
                    'The number of contracts traded so far today',
                    'The implied volatility level of the contract',
                    'The average trade size for the day'
                ],
                answer: 'The total number of outstanding contracts that have not been closed or exercised',
                explanation:
                    'Open Interest represents all active option contracts that remain open—i.e., those that haven’t been offset by an opposite trade, exercised, or expired.'
            },
            {
                question: 'A trade rapidly routed across multiple exchanges to capture the best possible prices is a _____?',
                options: ['Sweep Trade', 'Block Trade', 'Speed Print', 'Market Order'],
                answer: 'Sweep Trade',
                explanation:
                    'Trades labeled “sweep” indicate it was ordered to fill as fast as possible and indicates urgency.'
            },
            {
                question: 'If the number in the "Size" column is colored turqoise, what does that signify?',
                options: [
                    'The single trade size exceeds open interest',
                    'Daily volume exceeds open interest',
                    'Volume has not exceeded open interest',
                    'Contracts were sold instead of bought'
                ],
                answer: 'The single trade size exceeds open interest',
                explanation:
                    'Turqoise means that one individual trade was larger than the open interest. This is a strong signal of new demand in the contract. Bullflow tip: use the "Unusual" quick filter to find these trades easily.'
            },
            {
                question: 'SigScore measures _____?',
                options: [
                    'How unusual a trade is',
                    'Time decay of the option',
                    'Underlying market cap',
                    'Spread width'
                ],
                answer: 'How unusual a trade is',
                explanation:
                    'A higher Sig Score indicates the flow is more unusual. Bullflow uses a proprietary algorithm to calculate this score. Use this score as a reference to quickly identify trades worth investigating.'
            },
            {
                question: 'The delta column represents _____?',
                options: [
                    'Expected contract price change per $1 move of the underlying',
                    'Total number of contracts trading',
                    'Broker commission cost',
                    'Implied dividend yield'
                ],
                answer: 'Expected contract price change per $1 move of the underlying',
                explanation:
                    'Delta quantifies how much the option price moves for each $1 move in the underlying asset.'
            },
            {
                question: 'The IV column reflects _____?',
                options: [
                    'Market expectations of future price movement',
                    'Historical volatility',
                    'Interest‑rate sensitivity',
                    'Open‑interest growth'
                ],
                answer: 'Market expectations of future price movement',
                explanation:
                    'Implied Volatility (IV) represents the market’s forecast of future movement. Generally, higher IV indicates greater expected volatility.'
            },
            {
                question: 'OTM (%) tells you _____?',
                options: [
                    'How far contracts are in or out of the money as a percentage',
                    'Total open interest for the ticker',
                    'Volume relative to average',
                    'Earnings surprise percentage'
                ],
                answer: 'How far contracts are in or out of the money as a percentage',
                explanation:
                    'OTM shows the distance between strike and underlying spot price as a percentage.'
            }
        ]
    },

    /* ===================== PLATFORM TAB ===================== */
    {
        id: 'finding',
        label: 'Finding High Conviction Trades',
        subtitle: 'Identify high-conviction flow on Bullflow',
        questions: [
            // 10 questions from Part 2 video (already crafted)
            {
                question: 'What is one of the best indicators of high-conviction flow?',
                options: [
                    'Put/call ratio',
                    'Repeated, focused sweeps on the same contract',
                    'Continuous bid side flow',
                    'Indicator crossovers'
                ],
                answer: 'Repeated, focused sweeps on the same contract',
                explanation:
                    'Repeated, focused sweeps indicates a trader is building a position and has a strong directional bias.'
            },
            {
                question: 'Why does consistency in the contract matter?',
                options: [
                    'It shows the trader has a clear thesis',
                    'It diversifies risk',
                    'It conceals the trader’s true intent',
                    'It maximizes time decay'
                ],
                answer: 'It shows the trader has a clear thesis',
                explanation:
                    'Buying the same contract repeatedly indicates the trader is executing a deliberate, directional strategy rather than simply probing the market or hedging.'
            },
            {
                question: 'What does repeated buying despite a rising contract price indicate?',
                options: [
                    'Strong conviction',
                    'Market illiquidity',
                    'Arbitrage opportunities',
                    'Spoofing activity'
                ],
                answer: 'Strong conviction',
                explanation:
                    'Persistent purchases even as the price climbs reflect buyers’ firm belief in a further move.'
            },
            {
                question: 'Ask-side or above-ask fills usually indicate what type of order?',
                options: [
                    'Buy order',
                    'Sell order',
                    'Passive midpoint fill',
                    'Sweep order'
                ],
                answer: 'Buy order',
                explanation:
                    'Generally, if a trade is filled on the ask side, it\'s most likely a buy order. If it is above ask, it\'s even more likely a buy.'
            },
            {
                question: 'Why are sweep orders preferred over block trades for spotting high-conviction flow?',
                options: [
                    'They indicate urgency across multiple exchanges',
                    'They always incur higher fees',
                    'They signal bearish sentiment',
                    'They conceal the true order size'
                ],
                answer: 'They indicate urgency across multiple exchanges',
                explanation:
                    'Sweep orders split a large order across exchanges to fill immediately and capture the best possible prices. This shows the trader’s strong conviction and urgency.'
            },
            {
                question: 'Volume exceeding open interest increases conviction because _____',
                options: [
                    'It shows new positions are being opened beyond existing contracts',
                    'It means traders are closing positions en masse',
                    'It indicates the market is illiquid',
                    'It reflects only intraday noise'
                ],
                answer: 'It shows new positions are being opened beyond existing contracts',
                explanation:
                    'When volume surpasses open interest, more contracts are traded than previously existed, signaling fresh demand rather than routine activity.'
            },

            {
                question: 'Flow with mixed strikes, expirations, and sentiment should generally be ____.',
                options: ['Ignored', 'A sure‑fire winner', 'Shorted immediately', 'Considered only if IV low'],
                answer: 'Ignored',
                explanation:
                    'Options flow is full of trades that are not high conviction. If you see a trade with mixed strikes, expirations, and sentiment, it is likely not worth your time.'
            },
            {
                question: 'For high volume tickers like Nvidia, Tesla, Apple - meaningful flow is distinguished by ____.',
                options: [
                    'Much larger than typical trade values',
                    '$30k trades',
                    'Odd lots',
                    'High gamma exposure'
                ],
                answer: 'Much larger than typical trade values',
                explanation:
                    'In extremely liquid names, only orders that far exceed the norm reveal true smart money conviction and warrant attention.'
            },
            {
                question: 'What does seeing both bid-side and ask-side sweeps in quick succession indicate?',
                options: [
                    'Uncertain conviction',
                    'Impending breakout',
                    'Market-maker buildup',
                    'Risk-free arbitrage'
                ],
                answer: 'Uncertain conviction',
                explanation:
                    'When trades hit both bid and ask, it shows neither buyers nor sellers are firmly in control, creating ambiguous flow signals. Look for majority ask side flow (at least 75%)'
            },
            {
                question: 'Why are Ask and Above Ask (AA) trades important for finding high-conviction flow?',
                options: [
                    'They show traders are willing to pay a premium to get filled quickly',
                    'They help reduce implied volatility',
                    'They are typically part of hedging strategies',
                    'They indicate trades are being executed passively'
                ],
                answer: 'They show traders are willing to pay a premium to get filled quickly',
                explanation:
                    'Ask and Above Ask trades reflect urgency—buyers are lifting the offer or paying above it, which suggests strong conviction and a desire to enter a position immediately.'
            },
            {
                question: 'Why are Ask and Above Ask (AA) trades important for finding high-conviction flow?',
                options: [
                    'They show traders are willing to pay a premium to get filled quickly',
                    'They help reduce implied volatility',
                    'They are typically part of hedging strategies',
                    'They indicate trades are being executed passively'
                ],
                answer: 'They show traders are willing to pay a premium to get filled quickly',
                explanation:
                    'Ask and Above Ask trades reflect urgency—buyers are lifting the offer or paying above it, which suggests strong conviction and a desire to enter a position immediately.'
            },
            {
                question: 'Which quick filters/algo alerts are best for spotting aggresive, repeat, high-conviction flow?',
                options: [
                    'Urgent, Bullflow, and Position Builder',
                ],
                answer: 'Urgent, Bullflow, and Position Builder',
                explanation:
                    'Use these filters to find trades that meet multiple high conviction trade criteria in different time frames. These filters are a product of Bullflow\'s algorithm.'
            },
        ]
    },

    /* ===================== FINDING TRADES TAB ===================== */
    {
        id: 'platform',
        label: 'Using the Platform',
        subtitle: 'Learn to use Bullflow like a pro',
        questions: [
            {
                question: 'Which feature helps you filter flow at the click of a button?',
                options: ['Quick filters', 'Historical flow', 'Trade Menu', 'Alerts'],
                answer: 'Quick filters',
                explanation:
                    'Quick filters sit atop the scanner for one‑click filtering.'
            },
            {
                question: 'How do you learn what a specific quick filter does?',
                options: ['Hover over it for a tooltip', 'Open documentation', 'Enable debug mode', 'Right‑click and inspect'],
                answer: 'Hover over it for a tooltip',
                explanation:
                    'Hovering displays a pop‑up description.'
            },
            {
                question: 'What button resets all quick filters to default?',
                options: ['Reset/clear filters button', 'Reload page', 'Ctrl+R', 'Snail icon'],
                answer: 'Reset/clear filters button',
                explanation:
                    'The clear‑filters button sits at the top right and will reset your flow to the base state.'
            },
            {
                question: 'The “Urgent” quick filter highlights flow where traders are _____.',
                options: [
                    'Rapidly rushing into positions',
                    'Rolling old positions',
                    'Selling covered calls',
                    'Executing risk‑reversals'
                ],
                answer: 'Rapidly rushing into positions',
                explanation:
                    'The Urgent, Bullflow and Position Builder filters are a product of Bullflow\'s algorithm and will show you trades that meet multiple high conviction trade criteria in different time frames.'
            },
            {
                question: 'Where do favorite tickers appear in the UI?',
                options: [
                    'Above the quick filters',
                    'Left navigation',
                    'Footer',
                    'In a modal window'
                ],
                answer: 'Above the quick filters',
                explanation:
                    'Favorite tickers populate the area right above quick filters.'
            },
            {
                question: 'Where can you save a ticker/contract to the favorites bar?',
                options: [
                    'From the trade menu when clicking a trade row',
                    'In the filters panel',
                    'In the ticker detail section',
                    'In the alerts panel'
                ],
                answer: 'From the trade menu when clicking a trade row',
                explanation:
                    'After saving as a favorite, just ene click isolates flow for that contract/ticker.'
            },
            {
                question: 'What does the snail button toggle?',
                options: ['Slow mode', 'Night mode', 'Backtest mode', 'Sweep mode'],
                answer: 'Slow mode',
                explanation:
                    'Snail slows the feed—ask‑side sweeps only and higher minimums—to reduce noise at the open.'
            },
            {
                question: 'How can you save an alert',
                options: ['In the filters panel, set your filers, then click Save as Alert', 'In the alerts panel', 'In the trade menu', 'Somewhere else'],
                answer: 'In the filters panel, set your filers, then click Save as Alert',
                explanation:
                    'You can save any filters you set as your own custom alert, and you will be notified when a trade meets that filter criteria.'
            },
            {
                question: 'Algo alerts in Bullflow are intended to _____.',
                options: [
                    'Prompt investigation, not serve as direct buy signals',
                    'Guarantee profit',
                    'Replace due diligence',
                    'Auto‑exercise options'
                ],
                answer: 'Prompt investigation, not serve as direct buy signals',
                explanation:
                    'Alerts highlight unusual flow that deserves a closer look.'
            },
            {
                question: 'Algo alerts in Bullflow are intended to _____.',
                options: [
                    'Prompt investigation, not serve as direct buy signals',
                    'Guarantee profit',
                    'Replace due diligence',
                    'Auto‑exercise options'
                ],
                answer: 'Prompt investigation, not serve as direct buy signals',
                explanation:
                    'Alerts highlight unusual flow that deserves a closer look.'
            },
            {
                question: 'How can you receive your custom Bullflow alerts on mobile?',
                options: [
                    'Enable phone push notifications',
                    'Email only',
                    'They cannot be delivered to phones',
                    'RSS feed'
                ],
                answer: 'Enable phone push notifications',
                explanation:
                    'Bullflow lets users push custom alerts directly to their phone.'
            },
            {
                question: 'Even high‑probability flow requires traders to have ____.',
                options: ['An exit plan and proper sizing', 'No stop‑loss', '100% capital allocation', 'Hedging with futures'],
                answer: 'An exit plan and proper sizing',
                explanation:
                    'High probability does not equal certainty—you still need to manage risk.'
            },
            {
                question: 'What’s the easiest to backtest my strategy?',
                options: [
                    'Check the Peak return or Daily High columns in Historical Flow',
                    'Replay tick data in a charting platform',
                    'Export raw option quotes and write custom scripts',
                    'Rely solely on live paper-trading results'
                ],
                answer: 'Check the Peak return or Daily High columns in Historical Flow',
                explanation:
                    'Those columns will give you an idea of how well each trade performed.'
            },
            {
                question: 'What does the Peak Return column indicate?',
                options: [
                    'The highest % return the trade experienced since it was executed',
                    'The highest intraday return of the trade on the date it was traded',
                    'The current % return of the trade',
                    'The overall average value of the trades performance'
                ],
                answer: 'The highest % return the trade experienced since it was executed',
                explanation:
                    'Peak return will give you a good idea of a trades performance over time. Peak return is calculated on the fly, so it cannot be sorted or filtered'
            },
            {
                question: 'What does the Daily High column indicate?',
                options: [
                    'The highest % return the trade experienced since it was executed',
                    'The highest intraday return of the trade on the date it was traded',
                    'The current % return of the trade',
                    'The overall average value of the trades performance'
                ],
                answer: 'The highest intraday return of the trade on the date it was traded',
                explanation:
                    'Daily high is great for those who want to download the data and run some tests to see which trades perform the best intraday.'
            },
        ]
    },
];

export function shuffleArray(arr) {
    return [...arr].sort(() => Math.random() - 0.5);
}

// Badge tiers with icon and color
export const badgeTiers = [
    { label: 'Skill Level: Novice', icon: User, color: 'bg-gray-500 text-white' },
    { label: 'Skill Level: Apprentice', icon: Star, color: 'bg-blue-500 text-white' },
    { label: 'Skill Level: Journeyman', icon: Award, color: 'bg-green-500 text-white' },
    { label: 'Skill Level: Pro', icon: Zap, color: 'bg-yellow-500 text-gray-900' },
    { label: 'Skill Level: Expert', icon: Crown, color: 'bg-purple-500 text-white' }
];

// Tutorial video definitions
export const educationContent = [
    { label: 'Options Flow Basics', href: 'https://www.youtube.com/watch?v=z5pzhXV79sE' },
    { label: 'Finding Good Flow', href: 'https://www.youtube.com/watch?v=EzYG9vIiJ6Q' },
    { label: 'Best Practices', href: 'https://www.youtube.com/watch?v=7HHQRyG3bgY' },
    { label: 'Actual Trade Example', href: 'https://www.youtube.com/watch?v=XZNmvGPXTbU' },
    { label: 'Actual Trade Example 2', href: 'https://www.youtube.com/watch?v=wKKzwk802EA' },
    { label: 'Finding Profitable Trades Blog', href: 'https://blog.bullflow.io/how-to-use-the-bullflow-options-scanner-to-find-profitable-trades/' },
];
